import { Button, Input, Label } from "@drs-ops/ui";
import { useEffect } from "react";
import { OriginSelector } from "@/components/trip-planner/OriginSelector";
import { VenueAutocomplete } from "@/components/trip-planner/VenueAutocomplete";
import { Card, CardContent } from "@/components/ui/card";
import type { OriginRequest, TripPlannerRequest } from "@/types/tripPlanner";

function todayPlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface WhereSectionProps {
  value: TripPlannerRequest;
  onChange: (v: TripPlannerRequest) => void;
  onSubmit: () => void;
  submitting: boolean;
  homeOfficeLabel: string;
}

export function WhereSection({
  value,
  onChange,
  onSubmit,
  submitting,
  homeOfficeLabel,
}: WhereSectionProps): React.ReactElement {
  useEffect(() => {
    if (value.trip_end < value.trip_start) {
      onChange({ ...value, trip_end: value.trip_start });
    }
  }, [value, onChange]);

  const canSubmit =
    value.venue_address.trim().length >= 2 &&
    (value.origin.type !== "address" || value.origin.address.trim().length >= 2) &&
    value.crew_size >= 1 &&
    value.crew_size <= 20 &&
    value.radius_miles >= 5 &&
    value.radius_miles <= 25 &&
    value.trip_start.length === 10 &&
    value.trip_end.length === 10 &&
    value.trip_end >= value.trip_start;

  return (
    <Card>
      <CardContent className="grid gap-4 p-6 md:grid-cols-2">
        <VenueAutocomplete value={value.venue_address} onChange={(v) => onChange({ ...value, venue_address: v })} />
        <OriginSelector
          value={value.origin}
          onChange={(o: OriginRequest) => onChange({ ...value, origin: o })}
          homeOfficeLabel={homeOfficeLabel}
        />
        <div>
          <Label htmlFor="trip_start">Trip start</Label>
          <Input
            id="trip_start"
            type="date"
            value={value.trip_start}
            onChange={(e) => onChange({ ...value, trip_start: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="trip_end">Trip end</Label>
          <Input
            id="trip_end"
            type="date"
            value={value.trip_end}
            onChange={(e) => onChange({ ...value, trip_end: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="crew_size">Crew size</Label>
          <Input
            id="crew_size"
            type="number"
            min={1}
            max={20}
            value={value.crew_size}
            onChange={(e) => onChange({ ...value, crew_size: Number(e.target.value) })}
          />
        </div>
        <div>
          <Label htmlFor="radius_miles">Hotel search radius ({value.radius_miles} mi)</Label>
          <Input
            id="radius_miles"
            type="range"
            min={5}
            max={25}
            step={1}
            value={value.radius_miles}
            onChange={(e) => onChange({ ...value, radius_miles: Number(e.target.value) })}
          />
        </div>
        <div className="md:col-span-2">
          <Button type="button" onClick={onSubmit} disabled={!canSubmit || submitting}>
            {submitting ? "Calculating…" : "Calculate"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function defaultRequest(): TripPlannerRequest {
  return {
    venue_address: "",
    origin: { type: "home_office" },
    crew_size: 6,
    trip_start: todayPlus(7),
    trip_end: todayPlus(9),
    radius_miles: 10,
  };
}
